import React, { useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useAutoTranslate, useSaveTranslation } from '../../hooks/useResource';
import { useDismissOnOutsideClick } from '../../hooks/useDismissOnOutsideClick';
import { TRANSLATION_LANGUAGES, DETECT_LANGUAGE } from '../../constants/translationLanguages';
import type { TranslationLanguage } from '../../constants/translationLanguages';
import type { Translation } from '../../types/resource';
import './TranslationPanel.scss';

interface TranslationPanelProps {
  translation?: Translation;
  projectId: string;
  collectionId: string;
  resourceId: string;
  /** Project-level setting that gates the machine-translation controls. */
  googleTranslateEnabled?: boolean;
}

function ToolbarButton({
  active,
  disabled,
  onClick,
  title,
  children,
}: {
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      className={`btn btn-default btn-sm${active ? ' active' : ''}`}
      title={title}
      disabled={disabled}
      // Keep the selection while the toolbar is clicked.
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

export default function TranslationPanel({
  translation,
  projectId,
  collectionId,
  resourceId,
  googleTranslateEnabled,
}: TranslationPanelProps) {
  const [language, setLanguage] = useState<TranslationLanguage>(DETECT_LANGUAGE);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  useDismissOnOutsideClick(menuRef, menuOpen, () => setMenuOpen(false));
  const [dirty, setDirty] = useState(false);
  const autoTranslate = useAutoTranslate(projectId, collectionId, resourceId);
  const save = useSaveTranslation(projectId, collectionId, resourceId);

  const editor = useEditor({
    extensions: [StarterKit],
    content: translation?.translatedText ?? '',
    onUpdate: () => setDirty(true),
  });

  // Pick up a newly fetched translation — a different document, or the result of an
  // auto-translate — without discarding edits the reviewer hasn't saved.
  const lastLoaded = useRef<string | undefined>(undefined);
  useEffect(() => {
    const incoming = translation?.translatedText ?? '';
    if (!editor || incoming === lastLoaded.current) return;
    lastLoaded.current = incoming;
    if (dirty) return;
    editor.commands.setContent(incoming, { emitUpdate: false });
  }, [editor, translation?.translatedText, dirty]);

  function handleSave() {
    if (!editor) return;
    save.mutate(editor.getHTML(), { onSuccess: () => setDirty(false) });
  }

  function handleAutoTranslate() {
    autoTranslate.mutate(language.id, { onSuccess: () => setDirty(false) });
  }

  const busy = autoTranslate.isPending;


  return (
    <div
      className="resource-translate-window"
      style={{ display: 'flex', flexDirection: 'column', height: '100%', padding: 10, boxSizing: 'border-box' }}
    >
      {googleTranslateEnabled && (
        <div style={{ marginBottom: 10, flexShrink: 0 }}>
          <div ref={menuRef} className={`btn-group${menuOpen ? ' open' : ''}`} title="Select Source Language">
            <button
              type="button"
              className="btn btn-default dropdown-toggle"
              disabled={busy}
              onClick={() => setMenuOpen((o) => !o)}
            >
              {busy && <i className="fas fa-spinner fa-spin" style={{ marginRight: 6 }} />}
              {language.label} <span className="caret" />
            </button>
            <ul className="dropdown-menu" role="menu" style={{ maxHeight: 200, overflowY: 'auto' }}>
              {TRANSLATION_LANGUAGES.map((lang) => (
                <li key={lang.id}>
                  <a
                    role="menuitem"
                    tabIndex={-1}
                    onClick={() => { setLanguage(lang); setMenuOpen(false); }}
                  >
                    <i
                      className={`far ${language.id === lang.id ? 'fa-check-square' : 'fa-square'}`}
                      style={{ marginRight: 6 }}
                    />
                    {lang.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          <button
            type="button"
            className="btn btn-primary"
            style={{ marginLeft: 4 }}
            disabled={busy}
            onClick={handleAutoTranslate}
          >
            <i className="fab fa-google" /> Translate
          </button>
        </div>
      )}

      <div className="btn-toolbar" style={{ flexShrink: 0, marginBottom: 6 }}>
        <div className="btn-group">
          <ToolbarButton title="Bold" active={editor?.isActive('bold')} onClick={() => editor?.chain().focus().toggleBold().run()}>
            <i className="fas fa-bold" />
          </ToolbarButton>
          <ToolbarButton title="Italic" active={editor?.isActive('italic')} onClick={() => editor?.chain().focus().toggleItalic().run()}>
            <i className="fas fa-italic" />
          </ToolbarButton>
          <ToolbarButton title="Underline" active={editor?.isActive('underline')} onClick={() => editor?.chain().focus().toggleUnderline().run()}>
            <i className="fas fa-underline" />
          </ToolbarButton>
          <ToolbarButton title="Strikethrough" active={editor?.isActive('strike')} onClick={() => editor?.chain().focus().toggleStrike().run()}>
            <i className="fas fa-strikethrough" />
          </ToolbarButton>
        </div>
        <div className="btn-group">
          <ToolbarButton title="Heading 1" active={editor?.isActive('heading', { level: 1 })} onClick={() => editor?.chain().focus().toggleHeading({ level: 1 }).run()}>
            H1
          </ToolbarButton>
          <ToolbarButton title="Heading 2" active={editor?.isActive('heading', { level: 2 })} onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}>
            H2
          </ToolbarButton>
          <ToolbarButton title="Heading 3" active={editor?.isActive('heading', { level: 3 })} onClick={() => editor?.chain().focus().toggleHeading({ level: 3 }).run()}>
            H3
          </ToolbarButton>
          <ToolbarButton title="Blockquote" active={editor?.isActive('blockquote')} onClick={() => editor?.chain().focus().toggleBlockquote().run()}>
            <i className="fas fa-quote-right" />
          </ToolbarButton>
          <ToolbarButton title="Code block" active={editor?.isActive('codeBlock')} onClick={() => editor?.chain().focus().toggleCodeBlock().run()}>
            <i className="fas fa-code" />
          </ToolbarButton>
        </div>
        <div className="btn-group">
          <ToolbarButton title="Bullet list" active={editor?.isActive('bulletList')} onClick={() => editor?.chain().focus().toggleBulletList().run()}>
            <i className="fas fa-list-ul" />
          </ToolbarButton>
          <ToolbarButton title="Numbered list" active={editor?.isActive('orderedList')} onClick={() => editor?.chain().focus().toggleOrderedList().run()}>
            <i className="fas fa-list-ol" />
          </ToolbarButton>
        </div>
        <div className="btn-group">
          <ToolbarButton title="Undo" disabled={!editor?.can().undo()} onClick={() => editor?.chain().focus().undo().run()}>
            <i className="fas fa-undo" />
          </ToolbarButton>
          <ToolbarButton title="Redo" disabled={!editor?.can().redo()} onClick={() => editor?.chain().focus().redo().run()}>
            <i className="fas fa-redo" />
          </ToolbarButton>
        </div>
        <div className="btn-group pull-right">
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={save.isPending || !editor}
            onClick={handleSave}
          >
            <i className={`fas ${save.isPending ? 'fa-circle-notch fa-spin' : 'fa-save'}`} /> Save
          </button>
        </div>
      </div>

      <div
        className="iio-translation-editor"
        style={{ flex: 1, minHeight: 0, overflow: 'auto', border: '1px solid #ccc', borderRadius: 3, padding: 8, background: '#fff' }}
      >
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}
